import { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { fetchList, api } from "@/lib/api";
import JobCard from "@/components/JobCard";
import SeoHead from "@/components/SeoHead";

const COUNTRIES = ["France", "UK", "Germany", "Spain", "Italy", "Netherlands", "Belgium", "Denmark", "Sweden", "Switzerland", "USA", "Canada"];
const SALARY_RANGES = ["Unknown", "< 50k€", "50-80k€", "80-120k€", "120-180k€", "180-250k€", "> 250k€"];

const Jobs = () => {
  const { t, lang } = useLang();
  const [filters, setFilters] = useState({
    country: "all", contract_type: "all", seniority: "all", work_mode: "all",
    time_type: "all", salary_range: "all", technology: "all", since: "all", q: "",
  });
  const [items, setItems] = useState([]);
  const [techs, setTechs] = useState([]);

  useEffect(() => {
    api.get("/jobs/technologies").then((r) => setTechs(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = { ...filters, language: lang, limit: 100 };
    delete params.salary_range; // client-side filter
    fetchList("/jobs", params).then((data) => {
      const sr = filters.salary_range;
      setItems(sr && sr !== "all" ? data.filter((j) => (j.salary_range || "") === sr) : data);
    }).catch(() => {});
  }, [filters, lang]);

  const allOpt = { value: "all", label: t.filters.all };
  const Selector = ({ label, k, options, testid }) => (
    <label className="flex flex-col gap-1 text-[12px]">
      <span className="dl-overline">{label}</span>
      <select
        data-testid={testid}
        value={filters[k]}
        onChange={(e) => setFilters({ ...filters, [k]: e.target.value })}
        className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
      >
        {[allOpt, ...options].map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );

  const periodOpts = [
    { value: "today", label: t.filters.periodToday },
    { value: "week", label: t.filters.periodWeek },
    { value: "month", label: t.filters.periodMonth },
    { value: "quarter", label: t.filters.periodQuarter },
    { value: "year", label: t.filters.periodYear },
  ];

  return (
    <div data-testid="jobs-page" className="dl-container py-14">
      <SeoHead
        lang={lang}
        path="/jobs"
        title={lang === "fr" ? "Offres d'emploi Supply Chain IA" : "Supply Chain AI jobs"}
        description={lang === "fr"
          ? "Postes IA en Supply Chain, planification, S&OP et data. Signaux de marché et opportunités curées par Dataleo pour les Opérations."
          : "AI roles in Supply Chain, planning, S&OP and data. Market signals and curated opportunities from Dataleo for Operations leaders."}
      />
      <div className="border-b border-[#111827] pb-4 mb-8">
        <div className="dl-overline text-[#00A878]">Feed · Jobs</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 text-[#111827]">{t.nav.jobs}</h1>
        <p className="mt-3 text-[#4B5563] max-w-2xl">{lang === "fr" ? "Signaux de marché à travers les recrutements IA en Opérations." : "Market signals from hires in AI for Operations."}</p>
      </div>
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <Selector testid="job-filter-country" label={t.filters.country} k="country" options={COUNTRIES.map((v) => ({ value: v, label: v }))} />
        <Selector testid="job-filter-contract" label={lang === "fr" ? "Type de contrat" : "Contract type"} k="contract_type" options={["Permanent", "Fixed-term", "Mission", "Freelance", "Internship", "Apprenticeship"].map((v) => ({ value: v, label: v }))} />
        <Selector testid="job-filter-seniority" label={t.filters.seniority} k="seniority" options={["Junior", "Mid", "Senior", "Director", "VP / C-level"].map((v) => ({ value: v, label: v }))} />
        <Selector testid="job-filter-workmode" label={t.filters.workMode} k="work_mode" options={["Remote", "Hybrid", "Onsite"].map((v) => ({ value: v, label: v }))} />
        <Selector testid="job-filter-time" label={lang === "fr" ? "Temps" : "Time"} k="time_type" options={["Full-time", "Part-time"].map((v) => ({ value: v, label: v }))} />
        <Selector testid="job-filter-salary" label={lang === "fr" ? "Salaire (€)" : "Salary (€)"} k="salary_range" options={SALARY_RANGES.map((v) => ({ value: v, label: v }))} />
        <Selector testid="job-filter-tech" label={lang === "fr" ? "Technologie" : "Technology"} k="technology" options={techs.map((v) => ({ value: v, label: v }))} />
        <Selector testid="job-filter-period" label={t.filters.period} k="since" options={periodOpts} />
        <button
          data-testid="job-filter-reset"
          onClick={() => setFilters({ country: "all", contract_type: "all", seniority: "all", work_mode: "all", time_type: "all", salary_range: "all", technology: "all", since: "all", q: "" })}
          className="text-sm underline-offset-4 hover:underline text-[#4B5563]"
        >{t.filters.reset}</button>
      </div>
      <div className="grid md:grid-cols-2 gap-6" data-testid="jobs-grid">
        {items.map((j, i) => <JobCard key={j.id} item={j} idx={i} />)}
      </div>
    </div>
  );
};

export default Jobs;
